/** Animated integration-flow diagram: systems → AI hub → gVisor sandbox → live.
 *
 * One responsive SVG (everything scales with the viewBox). Motion inventory is
 * deliberately small: data packets travel the paths (SMIL animateMotion — no
 * pixel drift, scales with the SVG), the hub emits two pulse rings, dashed
 * paths crawl. Under prefers-reduced-motion the packets are hidden via CSS
 * (.flow-dot) and the rest freezes into a completed diagram.
 */

const SOURCES = [
  { y: 52, label: "Salesforce", color: "#00A1E0" },
  { y: 152, label: "SAP", color: "#0FAAFF" },
  { y: 252, label: "PostgreSQL", color: "#4169E1" },
];

// Shared path constants — the visible path and its packet ride the same `d`.
const P_IN = [
  "M196 74 C 300 74, 340 160, 424 160",
  "M196 174 C 290 174, 330 162, 424 160",
  "M196 274 C 300 274, 340 164, 424 160",
];
const P_TEST = "M540 160 L 626 160";
const P_LIVE = "M806 160 L 856 160";

function Packet({ path, begin, dur = 2.8 }: { path: string; begin: number; dur?: number }) {
  return (
    <circle className="flow-dot" r="4.5" fill="url(#flow-packet)">
      <animateMotion dur={`${dur}s`} begin={`${begin}s`} repeatCount="indefinite" path={path} />
    </circle>
  );
}

export function IntegrationFlow() {
  return (
    <div className="relative">
      <svg
        viewBox="0 0 960 320"
        className="w-full"
        role="img"
        aria-label="Integration flow: your systems connect through the Wise 2.0 AI hub, are verified in a gVisor sandbox, and go live"
      >
        <defs>
          <linearGradient id="flow-hub" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#2f6bad" />
            <stop offset="0.55" stopColor="#22568f" />
            <stop offset="1" stopColor="#7b4dff" />
          </linearGradient>
          <radialGradient id="flow-packet">
            <stop offset="0%" stopColor="#7b4dff" />
            <stop offset="100%" stopColor="#3b82f6" />
          </radialGradient>
        </defs>

        {/* connector paths */}
        {P_IN.map((d) => (
          <path key={d} d={d} className="flow-path" fill="none" stroke="#c7d8f5" strokeWidth="2" />
        ))}
        <path d={P_TEST} className="flow-path" fill="none" stroke="#c7d8f5" strokeWidth="2" />
        <path d={P_LIVE} className="flow-path" fill="none" stroke="#a7e5c9" strokeWidth="2" />

        {/* source system chips */}
        {SOURCES.map((s) => (
          <g key={s.label} className="flow-node">
            <rect x="16" y={s.y - 22} width="180" height="44" rx="22" fill="#fff" stroke="#e2e8f0" />
            <circle cx="44" cy={s.y} r="5" fill={s.color} />
            <text x="62" y={s.y + 5} fontSize="15" fontWeight="600" fill="#334155">
              {s.label}
            </text>
          </g>
        ))}

        {/* AI hub */}
        <circle cx="482" cy="160" r="44" fill="none" stroke="#7b4dff" strokeOpacity="0.35" strokeWidth="2" className="hub-ring" />
        <circle cx="482" cy="160" r="44" fill="none" stroke="#3b82f6" strokeOpacity="0.35" strokeWidth="2" className="hub-ring" style={{ animationDelay: "1.5s" }} />
        <rect x="424" y="102" width="116" height="116" rx="28" fill="url(#flow-hub)" />
        {/* sparkle glyph */}
        <path
          d="M482 128c2.9 13 5.9 16 19 19-13.1 2.9-16.1 5.9-19 19-2.9-13.1-5.9-16.1-19-19 13.1-2.9 16.1-6 19-19Z"
          fill="#fff"
        />
        <path
          d="M502 168c1.4 6.2 2.8 7.6 9 9-6.2 1.4-7.6 2.8-9 9-1.4-6.2-2.8-7.6-9-9 6.2-1.4 7.6-2.8 9-9Z"
          fill="#fff"
          opacity="0.85"
        />
        <text x="482" y="238" fontSize="13" fontWeight="700" fill="#475569" textAnchor="middle">
          Wise 2.0 AI
        </text>
        <text x="482" y="256" fontSize="11" fill="#94a3b8" textAnchor="middle">
          writes the connector
        </text>

        {/* sandbox card */}
        <g className="flow-node">
          <rect x="626" y="118" width="180" height="84" rx="16" fill="#fff" stroke="#e2e8f0" />
          <text x="646" y="146" fontSize="14" fontWeight="700" fill="#334155">
            gVisor sandbox
          </text>
          <circle cx="652" cy="166" r="6" fill="#d1fae5" stroke="#10b981" />
          <path d="M649.5 166l2 2 3.5-4" stroke="#059669" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <text x="666" y="171" fontSize="12" fill="#64748b">
            7/7 tests passed
          </text>
          <text x="646" y="191" fontSize="11" fill="#94a3b8">
            isolated · no network
          </text>
        </g>

        {/* live node */}
        <g>
          <rect x="856" y="136" width="92" height="48" rx="24" fill="#ecfdf5" stroke="#a7f3d0" />
          <circle cx="880" cy="160" r="5" fill="#10b981" />
          <text x="894" y="165" fontSize="14" fontWeight="700" fill="#047857">
            Live
          </text>
          <text x="902" y="204" fontSize="11" fill="#94a3b8" textAnchor="middle">
            signed XML
          </text>
        </g>

        {/* traveling data packets */}
        {P_IN.map((d, i) => (
          <Packet key={d} path={d} begin={i * 0.9} />
        ))}
        <Packet path={P_TEST} begin={0.6} dur={1.6} />
        <Packet path={P_TEST} begin={2.0} dur={1.6} />
        <Packet path={P_LIVE} begin={1.2} dur={1.2} />
      </svg>
    </div>
  );
}
